import { CSSProperties, useCallback, useEffect, useState } from "react";
import { Search, Plus, X, ArrowDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

import { addRetailerBalanceApi, debitRetailerWalletApi, getRetailersApi, verifyMpinApi } from "../api/api";

interface Retailer {
  id: number;
  name: string;
  fullname?: string;
  mobile?: string;
  mobile_no?: string;
  email?: string;
  wallet_balance?: number;
  status?: string;
  login_id?: number;
  wallet?: {
    amount?: number;
  };
}

export default function AddBalancePage() {
  const { user } = useAuth() as any;
  const navigate = useNavigate();

  const [retailers,   setRetailers]   = useState<Retailer[]>([]);
  const [loading,     setLoading]     = useState(false);
  const [search,      setSearch]      = useState("");
  const [error,       setError]       = useState<string | null>(null);

  // Popup state
  const [showPopup,   setShowPopup]   = useState(false);
  const [selected,    setSelected]    = useState<Retailer | null>(null);
  const [amount,      setAmount]      = useState("");
  const [description, setDescription] = useState("Wallet top-up");
  const [submitting,  setSubmitting]  = useState(false);
  const [successMsg,  setSuccessMsg]  = useState<string | null>(null);
  const [popupError,  setPopupError]  = useState<string | null>(null);
  const [mode,        setMode]        = useState<"credit" | "debit">("credit");
  const [mpinInput, setMpinInput] = useState("");
  const [verifyingMpin, setVerifyingMpin] = useState(false);
  const [mpinError, setMpinError] = useState<string | null>(null);

  // ── Fetch retailers ────────────────────────────────────────────────────────
  const fetchRetailers = useCallback(async () => {
    if (!user?.token) return;
    setLoading(true);
    setError(null);
    try {
      const body = await getRetailersApi(
        { search: search || undefined },
        user.token
      );
      if (body?.success) {
        const mapped = (body.data?.data ?? body.data ?? []).map((item: any) => ({
          id: item?.retailer_id ?? item?.id,
          name: item?.fullname ?? item?.name ?? "",
          fullname: item?.fullname ?? item?.name ?? "",
          mobile: item?.mobile_no ?? item?.mobile,
          mobile_no: item?.mobile_no ?? item?.mobile,
          email: item?.email,
          login_id: item?.login_id ?? item?.login_id_fk,
          wallet: item?.wallet,
        }));
        setRetailers(mapped);
      } else {
        setError(body?.message || "Failed to load retailers");
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load retailers");
    } finally {
      setLoading(false);
    }
  }, [user?.token, search]);

  useEffect(() => {
    fetchRetailers();
  }, [fetchRetailers]);

  // ── Popup helpers ──────────────────────────────────────────────────────────
  const openPopup = (retailer: Retailer, actionMode: "credit" | "debit" = "credit") => {
    setSelected(retailer);
    setMode(actionMode);
    setAmount("");
    setDescription(actionMode === "credit" ? "Wallet top-up" : "Wallet debit for settlement");
    setPopupError(null);
    setSuccessMsg(null);
    setMpinError(null);
    setMpinInput("");
    setShowPopup(true);
  };

  const closePopup = () => {
    setShowPopup(false);
    setSelected(null);
    setPopupError(null);
    setSuccessMsg(null);
    setMpinError(null);
    setMpinInput("");
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleTransaction = async () => {
    setPopupError(null);
    setMpinError(null);
    const parsed = parseFloat(amount);
    if (!amount || isNaN(parsed) || parsed <= 0) {
      return setPopupError("Enter a valid amount");
    }
    if (!selected) return;
    if (!user?.token) return setPopupError("Not authenticated");
    if (!mpinInput.trim()) return setMpinError("Enter MPIN to proceed");

    setVerifyingMpin(true);
    try {
      const verifyBody = await verifyMpinApi(user.id, mpinInput.trim(), user.token);
      const verifyStatus = verifyBody?.data?.verify_status ?? false;
      if (!verifyStatus) {
        return setMpinError(verifyBody?.data?.message || "Invalid MPIN");
      }
    } catch (e: unknown) {
      setMpinError(e instanceof Error ? e.message : "MPIN verification failed");
      return;
    } finally {
      setVerifyingMpin(false);
    }

    setSubmitting(true);
    try {
      const defaultDescription = mode === "credit" ? "Wallet top-up" : "Wallet debit for settlement";
      const apiCall = mode === "credit" ? addRetailerBalanceApi : debitRetailerWalletApi;

      const body = await apiCall(
        selected.login_id,
        parsed,
        description || defaultDescription,
        user.token
      );

      if (body?.success) {
        const actionText = mode === "credit" ? "added" : "debited";
        setSuccessMsg(body.message || `Balance ${actionText} successfully`);
        setMpinInput("");
        fetchRetailers();
      } else {
        setPopupError(body?.message || `Failed to ${mode} balance`);
      }
    } catch (e: unknown) {
      setPopupError(e instanceof Error ? e.message : `Failed to ${mode} balance`);
    } finally {
      setSubmitting(false);
    }
  };

  const formatINR = (val?: number) => {
    if (val == null) return "—";
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(val);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      <h1 style={s.heading}>Add Balance</h1>

      {/* Search */}
      <div style={s.searchRow}>
        <div style={s.searchWrap}>
          <Search size={16} color="#94a3b8" />
          <input
            style={s.searchInput}
            placeholder="Search retailer name / mobile…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {error && <div style={s.errBanner}>{error}</div>}

      {/* Table */}
      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr style={s.thead}>
              <th style={s.th}>#</th>
              <th style={s.th}>Name</th>
              <th style={s.th}>Mobile</th>
              <th style={s.th}>Email</th>
              <th style={s.th}>Wallet Balance</th>
              <th style={s.th}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={s.center}>Loading…</td>
              </tr>
            ) : retailers.length === 0 ? (
              <tr>
                <td colSpan={7} style={s.center}>No retailers found</td>
              </tr>
            ) : (
              retailers.map((r, idx) => (
                <tr key={r.id} style={idx % 2 === 0 ? s.rowEven : s.rowOdd}>
                  <td style={s.td}>{idx + 1}</td>
                  <td style={s.td}>
                    <button
                      type="button"
                      onClick={() =>
                        navigate("/retailers", {
                          state: {
                            retailerId: r.id,
                            retailerLoginId: r.login_id ?? r.id,
                            retailerName: r.fullname || r.name,
                          },
                        })
                      }
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: 0,
                        color: "#2563eb",
                        cursor: "pointer",
                        fontWeight: 600,
                        textAlign: "left",
                      }}
                    >
                      {r.fullname}
                    </button>
                  </td>
                  <td style={s.td}>{r.mobile_no || "—"}</td>
                  <td style={s.td}>{r.email || "—"}</td>
                  <td style={{ ...s.td, fontWeight: 600, color: "#2563eb" }}>
                    ₹ {formatINR(r.wallet?.amount)}
                  </td>
                
                  <td style={s.td}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button style={s.addBtn} onClick={() => openPopup(r, "credit")}>
                        <Plus size={14} />
                        Credit
                      </button>
                      <button style={s.debitBtn} onClick={() => openPopup(r, "debit")}>
                        <ArrowDown size={14} />
                        Debit
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Popup ── */}
      {showPopup && selected && (
        <div style={s.overlay}>
          <div style={s.modal}>

            {/* Header */}
            <div style={s.modalHeader}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>
                {mode === "credit" ? "Add Balance" : "Debit Wallet"} — {selected.fullname || selected.name}
              </span>
              <button style={s.closeBtn} onClick={closePopup}>
                <X size={18} />
              </button>
            </div>

            {/* Retailer info strip */}
            <div style={s.infoStrip}>
              <div style={s.infoItem}>
                <span style={s.infoLabel}>Current Balance</span>
                <span style={{ ...s.infoVal, color: "#2563eb" }}>
                  ₹ {formatINR(selected.wallet?.amount)}
                </span>
              </div>
              {selected.mobile && (
                <div style={s.infoItem}>
                  <span style={s.infoLabel}>Mobile</span>
                  <span style={s.infoVal}>{selected.mobile}</span>
                </div>
              )}
            </div>

            {/* Success state */}
            {successMsg ? (
              <div style={s.successBox}>
                <div style={{ fontSize: 32, marginBottom: 6 }}>✓</div>
                <div>{successMsg}</div>
                <button
                  style={{ ...s.addBtn, marginTop: 16, justifyContent: "center", width: "100%" }}
                  onClick={closePopup}
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                {/* Amount */}
                <div style={s.fieldGroup}>
                  <label style={s.label}>Amount (₹) *</label>
                  <input
                    style={s.input}
                    type="number"
                    min="1"
                    placeholder="e.g. 500"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleTransaction()}
                  />
                </div>

                {/* Description */}
                <div style={s.fieldGroup}>
                  <label style={s.label}>Description</label>
                  <input
                    style={s.input}
                    type="text"
                    placeholder={mode === "credit" ? "Wallet top-up" : "Debit reason"}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>

                <div style={s.fieldGroup}>
                  <label style={s.label}>Enter MPIN to confirm *</label>
                  <input
                    style={s.input}
                    type="password"
                    placeholder="Enter MPIN"
                    value={mpinInput}
                    onChange={(e) => setMpinInput(e.target.value.replace(/\D/g, ""))}
                    maxLength={6}
                  />
                  {mpinError && <div style={{ color: "#d9534f", marginTop: 6, fontSize: 13 }}>{mpinError}</div>}
                </div>

                {popupError && <div style={s.errBanner}>{popupError}</div>}

                {/* Actions */}
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    style={mode === "credit" ? { ...s.addBtn, flex: 1, justifyContent: "center" } : { ...s.debitBtn, flex: 1, justifyContent: "center" }}
                    onClick={handleTransaction}
                    disabled={submitting || verifyingMpin}
                  >
                    {submitting || verifyingMpin ? "Processing…" : mode === "credit" ? "Confirm & Add" : "Confirm & Debit"}
                  </button>
                  <button
                    style={s.cancelBtn}
                    onClick={closePopup}
                    disabled={submitting || verifyingMpin}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}

          </div>
        </div>
      )}
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────

const s: Record<string, CSSProperties> = {
  heading:     { fontSize: 26, fontWeight: 700, marginBottom: 20 },
  searchRow:   { marginBottom: 16, display: "flex" },
  searchWrap:  {
    display: "flex", alignItems: "center", gap: 8,
    background: "#fff", border: "1px solid #e2e8f0",
    borderRadius: 8, padding: "8px 12px", width: 320,
  },
  searchInput: {
    border: "none", outline: "none", fontSize: 14,
    width: "100%", background: "transparent",
  },
  tableWrap: {
    background: "#fff", borderRadius: 10,
    boxShadow: "0 4px 16px rgba(15,23,42,0.06)", overflow: "auto",
  },
  table:   { width: "100%", borderCollapse: "collapse" },
  thead:   { background: "#f8fafc" },
  th: {
    padding: "12px 14px", textAlign: "left", fontSize: 13,
    fontWeight: 600, color: "#64748b",
    borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap",
  },
  td:      { padding: "12px 14px", fontSize: 14, color: "#1e293b" },
  rowEven: { background: "#fff" },
  rowOdd:  { background: "#fafafa" },
  center:  { padding: 24, textAlign: "center", color: "#94a3b8" },
  badge: {
    display: "inline-block", padding: "2px 10px",
    borderRadius: 20, fontSize: 12, fontWeight: 600,
  },
  addBtn: {
    display: "inline-flex", alignItems: "center", gap: 4,
    background: "#2563eb", color: "#fff", border: "none",
    padding: "7px 12px", borderRadius: 7, cursor: "pointer",
    fontSize: 13, fontWeight: 500,
  },
  debitBtn: {
    display: "inline-flex", alignItems: "center", gap: 4,
    background: "#dc2626", color: "#fff", border: "none",
    padding: "7px 12px", borderRadius: 7, cursor: "pointer",
    fontSize: 13, fontWeight: 500,
  },
  cancelBtn: {
    background: "transparent", color: "#64748b",
    border: "1px solid #e2e8f0", padding: "7px 16px",
    borderRadius: 7, cursor: "pointer", fontSize: 13,
  },
  errBanner: {
    background: "#fef2f2", color: "#dc2626",
    border: "1px solid #fecaca", borderRadius: 8,
    padding: "10px 14px", fontSize: 14,
  },
  overlay: {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex", alignItems: "center",
    justifyContent: "center", zIndex: 9999,
  },
  modal: {
    background: "#fff", borderRadius: 12, padding: 24,
    width: 420, maxWidth: "92vw",
    boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
    display: "flex", flexDirection: "column", gap: 16,
  },
  modalHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
  },
  closeBtn: {
    background: "transparent", border: "none",
    cursor: "pointer", color: "#64748b",
    display: "flex", alignItems: "center",
  },
  infoStrip: {
    display: "flex", gap: 24, background: "#f8fafc",
    borderRadius: 8, padding: "10px 14px",
  },
  infoItem:   { display: "flex", flexDirection: "column", gap: 2 },
  infoLabel:  { fontSize: 11, color: "#94a3b8", fontWeight: 500 },
  infoVal:    { fontSize: 15, fontWeight: 700, color: "#1e293b" },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 6 },
  label:  { fontSize: 13, fontWeight: 600, color: "#374151" },
  input:  {
    padding: "10px 12px", border: "1px solid #e2e8f0",
    borderRadius: 8, fontSize: 14, outline: "none",
  },
  successBox: {
    textAlign: "center", color: "#16a34a",
    fontWeight: 600, fontSize: 15, padding: "8px 0",
  },
};